import type { DomainConfiguration } from '../ConfigurationMapper';
import type { RulesContext } from './context';
import { getServicePartNumbers, snapToDuration } from '../../utils/partNumberUtils';
import { getProductPrice } from '../../utils/configUtilities';

// Ported from useConfigurationLogic.jsx applySharedBusinessRules:
//   Rule 8b (service option reset)        :419-422
//   Rule 4  (pricing on quarter/service)   :514-544
//   Rule 5  (pricing on registration)      :547-557
//   Rule 7  (vendor-forced consultation)   :560-562
// Rule 8 (E2E compression) lives in storageRules.ts alongside the other storage-block rules.

// Rule 8b: Reset serviceOption when the current selection isn't valid for the active vendor
// (e.g. after a vendor switch).
export function applyServiceOptionReset(next: DomainConfiguration, ctx: RulesContext): void {
  const valid = ctx.capabilities.serviceOptions.map((o: any) => o.key);
  if (!valid.includes(next.pricing.serviceOption)) {
    next.pricing.serviceOption = ctx.capabilities.serviceOptions[0].key;
  }
}

// Snap the subscription duration onto the vendor's declared terms (spec §5.4).
// Runs BEFORE applyPricingOnQuarterService so the snapped duration selects the SKU.
export function applySubscriptionDurationSnap(next: DomainConfiguration, ctx: RulesContext): void {
  const durations = (ctx.capabilities as any).subscriptionDurations as number[] | undefined;
  if (!durations?.length) return;
  const snapped = snapToDuration(next.pricing.subscriptionMonths, durations);
  if (snapped !== next.pricing.subscriptionMonths) next.pricing.subscriptionMonths = snapped;
}

// Rule 4: Update software pricing when quarter, service option, or subscription duration
// changes — but NOT while loading a saved config (ctx.metaState.isPricingLocked).
// Ported verbatim from :514-544; duration-change trigger added for term-licensed vendors (SMC).
export function applyPricingOnQuarterService(
  prev: DomainConfiguration, next: DomainConfiguration, ctx: RulesContext,
): void {
  if (ctx.metaState.isPricingLocked) return;
  const p = next.pricing;
  const changed = prev.pricing.quarter !== p.quarter || prev.pricing.serviceOption !== p.serviceOption
    || (!!(ctx.capabilities as any).subscriptionDurations?.length && prev.pricing.subscriptionMonths !== p.subscriptionMonths);
  if (!changed) return;

  const servicePartNumbers = getServicePartNumbers(
    p.serviceOption, { id: ctx.vendorId, capabilities: ctx.capabilities }, p.subscriptionMonths,
  );
  const newSSDPricing = getProductPrice(ctx.productDb, p.quarter, servicePartNumbers.ssd);
  const newHDDPricing = getProductPrice(ctx.productDb, p.quarter, servicePartNumbers.hdd);

  p.baseSsdSoftware = newSSDPricing;
  p.baseHddSoftware = newHDDPricing;
  // Apply registration multiplier
  const m = ctx.businessRules.registrationMultiplier;
  const isNoReg = next.viewState.isNoReg;
  p.ssdSoftware = isNoReg ? newSSDPricing : newSSDPricing * m;
  p.hddSoftware = isNoReg ? newHDDPricing : newHDDPricing * m;
  // Fetch and set Support Unit Prices (null support key = Software Only option, no HW support charge)
  p.ssdSupportUnitPrice = servicePartNumbers.ssd_support
    ? getProductPrice(ctx.productDb, p.quarter, servicePartNumbers.ssd_support)
    : 0;
  p.hddSupportUnitPrice = servicePartNumbers.hdd_support
    ? getProductPrice(ctx.productDb, p.quarter, servicePartNumbers.hdd_support)
    : 0;
  // Set License Unit Price based on new Software and Support prices
  p.ssdLicenseUnitPrice = p.ssdSoftware - p.ssdSupportUnitPrice;
  p.hddLicenseUnitPrice = p.hddSoftware - p.hddSupportUnitPrice;
}

// Rule 5: Update pricing when registration status changes.
export function applyPricingOnRegistration(
  prev: DomainConfiguration, next: DomainConfiguration, ctx: RulesContext,
): void {
  if (prev.viewState.isNoReg === next.viewState.isNoReg) return;
  const m = ctx.businessRules.registrationMultiplier;
  const p = next.pricing, noReg = next.viewState.isNoReg;
  p.ssdSoftware = noReg ? p.baseSsdSoftware : p.baseSsdSoftware * m;
  p.hddSoftware = noReg ? p.baseHddSoftware : p.baseHddSoftware * m;
  // Recalculate License Unit Price regardless - Support Unit Price doesn't change.
  p.ssdLicenseUnitPrice = p.ssdSoftware - p.ssdSupportUnitPrice;
  p.hddLicenseUnitPrice = p.hddSoftware - p.hddSupportUnitPrice;
}

// Rule 7: Vendor forces consultation mode (controlled via vendorRegistry businessRules.forceConsultationMode).
export function applyConsultationMode(next: DomainConfiguration, ctx: RulesContext): void {
  if (ctx.businessRules.forceConsultationMode) next.viewState.consultationMode = true;
}
